import React, { useState, useEffect, useCallback } from "react";
import { getMyTrips, createTrip, updateTrip, deleteTrip } from "../api/trips";
import "./MyTrips.css";

export default function MyTrips() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // all, planning, active, completed
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [formData, setFormData] = useState({
    destination: "",
    trip_date: "",
    trip_duration: "",
    budget: "",
    interests: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadTrips = useCallback(async () => {
    try {
      setLoading(true);
      const status = activeFilter === "all" ? null : activeFilter;
      const data = await getMyTrips(status);
      setTrips(data);
      setError(null);
    } catch (err) {
      console.error("Error loading trips:", err);
      setError("Failed to load trips. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  function openCreateModal() {
    setFormData({
      destination: "",
      trip_date: "",
      trip_duration: "",
      budget: "",
      interests: "",
      description: "",
    });
    setShowCreateModal(true);
  }

  function openEditModal(trip) {
    setSelectedTrip(trip);
    setFormData({
      destination: trip.destination,
      trip_date: trip.trip_date,
      trip_duration: trip.trip_duration,
      budget: trip.budget,
      interests: trip.interests,
      description: trip.description || "",
      status: trip.status,
    });
    setShowEditModal(true);
  }

  function closeModals() {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedTrip(null);
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  }

  async function handleCreateTrip(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTrip(formData);
      closeModals();
      loadTrips();
      setError(null);
    } catch (err) {
      console.error("Error creating trip:", err);
      setError("Failed to create trip. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateTrip(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await updateTrip(selectedTrip.id, formData);
      closeModals();
      loadTrips();
      setError(null);
    } catch (err) {
      console.error("Error updating trip:", err);
      setError("Failed to update trip. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteTrip(tripId) {
    if (!window.confirm("Are you sure you want to delete this trip?")) {
      return;
    }
    try {
      await deleteTrip(tripId);
      loadTrips();
      setError(null);
    } catch (err) {
      console.error("Error deleting trip:", err);
      setError("Failed to delete trip. Please try again.");
    }
  }

  async function handleMarkCompleted(tripId) {
    try {
      await updateTrip(tripId, { status: "completed" });
      loadTrips();
      setError(null);
    } catch (err) {
      console.error("Error marking trip as completed:", err);
      setError("Failed to update trip status.");
    }
  }

  async function handleMarkActive(tripId) {
    try {
      await updateTrip(tripId, { status: "active" });
      loadTrips();
      setError(null);
    } catch (err) {
      console.error("Error marking trip as active:", err);
      setError("Failed to update trip status.");
    }
  }

  function getStatusColor(status) {
    switch (status) {
      case "planning":
        return "status-planning";
      case "active":
        return "status-active";
      case "completed":
        return "status-completed";
      case "cancelled":
        return "status-cancelled";
      default:
        return "";
    }
  }

  function getTripIcon(status) {
    switch (status) {
      case "planning":
        return "📝";
      case "active":
        return "✈️";
      case "completed":
        return "✅";
      case "cancelled":
        return "❌";
      default:
        return "🗺️";
    }
  }

  if (loading) {
    return (
      <div className="my-trips-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Loading your trips...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-trips-container">
      <div className="container">
        {/* Header */}
        <div className="trips-header">
          <h1>🗺️ My Trips</h1>
          <button className="create-trip-btn" onClick={openCreateModal}>
            + Create New Trip
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Filters */}
        <div className="trip-filters">
          <button
            className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            All Trips
          </button>
          <button
            className={`filter-btn ${activeFilter === "planning" ? "active" : ""}`}
            onClick={() => setActiveFilter("planning")}
          >
            📝 Planning
          </button>
          <button
            className={`filter-btn ${activeFilter === "active" ? "active" : ""}`}
            onClick={() => setActiveFilter("active")}
          >
            ✈️ Active
          </button>
          <button
            className={`filter-btn ${activeFilter === "completed" ? "active" : ""}`}
            onClick={() => setActiveFilter("completed")}
          >
            ✅ Completed
          </button>
        </div>

        {/* Trips Grid */}
        {trips.length > 0 ? (
          <div className="trips-grid">
            {trips.map((trip) => (
              <div key={trip.id} className="trip-card">
                <div className="trip-card-header">
                  <span className={`trip-status ${getStatusColor(trip.status)}`}>
                    {getTripIcon(trip.status)} {trip.status}
                  </span>
                </div>
                <div className="trip-card-body">
                  <h3 className="trip-destination">📍 {trip.destination}</h3>
                  <div className="trip-details">
                    <div className="trip-detail">
                      <span className="detail-icon">📅</span>
                      <span className="detail-text">
                        {new Date(trip.trip_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="trip-detail">
                      <span className="detail-icon">⏱️</span>
                      <span className="detail-text">{trip.trip_duration}</span>
                    </div>
                    <div className="trip-detail">
                      <span className="detail-icon">💰</span>
                      <span className="detail-text">{trip.budget}</span>
                    </div>
                  </div>
                  {trip.interests && (
                    <div className="trip-interests">
                      {trip.interests.split(",").map((interest, idx) => (
                        <span key={idx} className="interest-tag">
                          {interest.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                  {trip.description && (
                    <p className="trip-description">{trip.description}</p>
                  )}
                </div>
                <div className="trip-card-actions">
                  <button
                    className="edit-btn"
                    onClick={() => openEditModal(trip)}
                  >
                    ✏️ Edit
                  </button>
                  {trip.status === "planning" && (
                    <button
                      className="activate-btn"
                      onClick={() => handleMarkActive(trip.id)}
                    >
                      ✈️ Mark Active
                    </button>
                  )}
                  {trip.status === "active" && (
                    <button
                      className="complete-btn"
                      onClick={() => handleMarkCompleted(trip.id)}
                    >
                      ✓ Mark Completed
                    </button>
                  )}
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteTrip(trip.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-trips">
            <div className="no-trips-icon">🗺️</div>
            <h2>No trips yet</h2>
            <p>Create your first trip to start finding travel buddies!</p>
            <button className="create-trip-btn-large" onClick={openCreateModal}>
              + Create Your First Trip
            </button>
          </div>
        )}
      </div>

      {/* Create Trip Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-content trip-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Trip</h2>
            <form onSubmit={handleCreateTrip}>
              <div className="form-group">
                <label>Destination *</label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  placeholder="e.g., Tokyo, Japan"
                  required
                />
              </div>
              <div className="form-group">
                <label>Trip Date *</label>
                <input
                  type="date"
                  name="trip_date"
                  value={formData.trip_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Duration *</label>
                <input
                  type="text"
                  name="trip_duration"
                  value={formData.trip_duration}
                  onChange={handleInputChange}
                  placeholder="e.g., 7 days"
                  required
                />
              </div>
              <div className="form-group">
                <label>Budget *</label>
                <input
                  type="text"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  placeholder="e.g., ₹2000-₹3000"
                  required
                />
              </div>
              <div className="form-group">
                <label>Interests *</label>
                <input
                  type="text"
                  name="interests"
                  value={formData.interests}
                  onChange={handleInputChange}
                  placeholder="e.g., food, art, adventure"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Tell others about your trip plans..."
                  rows="4"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeModals}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? "Creating..." : "Create Trip"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Trip Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-content trip-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Trip</h2>
            <form onSubmit={handleUpdateTrip}>
              <div className="form-group">
                <label>Destination *</label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Trip Date *</label>
                <input
                  type="date"
                  name="trip_date"
                  value={formData.trip_date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Duration *</label>
                <input
                  type="text"
                  name="trip_duration"
                  value={formData.trip_duration}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Budget *</label>
                <input
                  type="text"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Interests *</label>
                <input
                  type="text"
                  name="interests"
                  value={formData.interests}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeModals}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? "Updating..." : "Update Trip"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
